import { KpiService } from '@/services';
import { IKpiFamilyCohort } from '@/types/response';
import {
  Alert,
  Button,
  Card,
  Segmented,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { formatNumber } from './KpiChart';

const { Text } = Typography;

const WEEK_OPTIONS = [
  { label: '12週', value: 12 },
  { label: '26週', value: 26 },
];

/** 割合（%、小数1桁）。分母 0 は null */
const cohortRate = (count: number, families: number): number | null =>
  families ? Math.round((count / families) * 1000) / 10 : null;

/** 「12.5%（5）」。期限が来ていない週は数がまだ伸びるので薄く出す */
const RateCell: React.FC<{
  count: number;
  families: number;
  complete?: boolean;
}> = ({ count, families, complete = true }) => {
  const body = (
    <span className={complete ? 'text-fg' : 'text-fg-muted'}>
      {formatNumber(cohortRate(count, families), '%')}
      <span className='text-fg-muted text-[12px] ml-xs'>
        （{formatNumber(count)}）
      </span>
    </span>
  );

  return complete ? (
    body
  ) : (
    <Tooltip title='期限が来ていない家族がいるので、まだ伸びます'>
      {body}
    </Tooltip>
  );
};

/**
 * 登録週コホート。その週に作られた家族が、何%・何日で複数人家族になったか。
 * 「招待しない」のか「招待しても入らない」のかを、招待率と2人目の率の差で見る。
 */
export const FamilyCohortTable: React.FC = () => {
  const [weeks, setWeeks] = useState(12);
  const query = useQuery(['kpi-family-cohorts', weeks], () =>
    KpiService.familyCohorts({ weeks }),
  );
  const rows = query.data?.data ?? [];

  return (
    <Card
      size='small'
      className='lg:col-span-2'
      title='登録週ごとの複数人化'
      extra={
        <Segmented
          size='small'
          options={WEEK_OPTIONS}
          value={weeks}
          onChange={(v) => setWeeks(Number(v))}
        />
      }
    >
      <div className='text-fg-muted text-[12px] mb-sm'>
        その週に作られた家族のうち、作ってから7日以内に招待リンクを作った家族と、2人目が加わった家族の割合。
        招待リンクで登録した人は既存の家族に入るので含みません。薄い数字は、まだ期限が来ていない家族がいる週です（これから伸びます）。
      </div>
      {query.isError ? (
        // 取得失敗を「0%」に見せない
        <Alert
          type='error'
          showIcon
          message='取得できませんでした'
          description={
            <Space direction='vertical' size={4}>
              <Text>数字が出ていないのは「0」ではなく不明です。</Text>
              <Button size='small' onClick={() => query.refetch()}>
                再読み込み
              </Button>
            </Space>
          }
        />
      ) : (
        <Table
          size='small'
          rowKey='weekStart'
          loading={query.isLoading}
          pagination={false}
          scroll={{ x: 720, y: 400 }}
          dataSource={rows}
          locale={{ emptyText: '期間内に作られた家族はありません' }}
          columns={[
            {
              title: '登録週',
              dataIndex: 'weekStart',
              key: 'weekStart',
              width: 110,
              render: (v: string) =>
                `${moment(v, 'YYYY-MM-DD').format('YYYY/MM/DD')}〜`,
            },
            {
              title: '家族',
              dataIndex: 'families',
              key: 'families',
              align: 'right',
              width: 72,
              render: (v: number) => formatNumber(v),
            },
            {
              title: '7日以内に招待',
              key: 'invitedWithin7d',
              align: 'right',
              render: (_: unknown, r: IKpiFamilyCohort) => (
                <RateCell
                  count={r.invitedWithin7d}
                  families={r.families}
                  complete={r.complete7d}
                />
              ),
            },
            {
              title: '7日以内に2人目',
              key: 'multiWithin7d',
              align: 'right',
              render: (_: unknown, r: IKpiFamilyCohort) => (
                <RateCell
                  count={r.multiWithin7d}
                  families={r.families}
                  complete={r.complete7d}
                />
              ),
            },
            {
              title: '30日以内に2人目',
              key: 'multiWithin30d',
              align: 'right',
              render: (_: unknown, r: IKpiFamilyCohort) => (
                <RateCell
                  count={r.multiWithin30d}
                  families={r.families}
                  complete={r.complete30d}
                />
              ),
            },
            {
              title: '今までに2人目',
              key: 'multiToDate',
              align: 'right',
              render: (_: unknown, r: IKpiFamilyCohort) => (
                <RateCell count={r.multiToDate} families={r.families} />
              ),
            },
          ]}
        />
      )}
    </Card>
  );
};
